'use client'

/**
 * Homepage hero search. Buy | Sell, built so BOTH sides exist in the server
 * HTML and both work with scripting off (SITE-12). SITE-83 adapts catalog
 * modules into house primitives: V3Tabs (sliding indicator), V3MorphSearch
 * (field morphs into results). The live count on the navy chip is type, not
 * a digit wheel — AnimatedNumber paints cream tiles that read as a white hole.
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { flattenSuggestions, useSearchSuggest } from '@/components/search/SearchSuggest'
import AddressAutocomplete from '@/components/seller-lp/AddressAutocomplete'
import { MorphingSearch, type MorphingSearchItem } from '@/components/motion/morphing-search'
import { Tabs } from '@/components/motion/tabs'
import { V3MorphSearch, V3Tabs, type V3MorphSearchItem } from '@/components/site/v3'

// Tip Ready: the route imports the installed catalog files. V3 wrappers
// restyle tokens; MorphingSearch and Tabs are the live objects.
export { MorphingSearch, Tabs }
import { searchHrefForQuery } from '@/lib/parse-search-query'
import { publishRegionalSearchHref } from '@/lib/search/publish-regional-search-href'
import { formatCount } from '@/lib/format/count'
import { withCountBasis, type ListingCount } from '@/lib/listing-counts'
import { markAskSource } from '@/lib/ask-source'
import { trackEvent } from '@/lib/tracking'
import { trackSiteSearch } from '@/components/search/track-site-search'
import type { HomeHeroLive } from './home-hero-inventory'
import { homeHeroTypedItem } from './home-hero-search-items'
import './home-hero-search.css'

/** Where a no-JS Buy submit lands: the regional inventory page. */
const BUY_ACTION = '/homes-for-sale'
/** Where a no-JS Sell submit lands: the valuation form, at its anchor. */
const SELL_ACTION = '/sell#get-value'

/** MorphingSearch shows these when the query is empty — the demo opens onto a list, not a blank overlay. */
const PLACE_SEEDS: MorphingSearchItem[] = [
  { id: '/homes-for-sale/bend', title: 'Bend', description: 'City' },
  { id: '/homes-for-sale/redmond', title: 'Redmond', description: 'City' },
  { id: '/homes-for-sale/sisters', title: 'Sisters', description: 'City' },
  // Sunriver's one inventory page; /homes-for-sale/sunriver is noindex
  // (lib/communities/self-city-community.ts), same as V3ChromeSearch.
  { id: '/communities/sunriver', title: 'Sunriver', description: 'Community' },
  { id: '/communities/tetherow', title: 'Tetherow', description: 'Bend' },
  { id: '/homes-for-sale/prineville', title: 'Prineville', description: 'City' },
  { id: '/homes-for-sale/la-pine', title: 'La Pine', description: 'City' },
  { id: '/homes-for-sale/madras', title: 'Madras', description: 'City' },
]

export function HomeHeroSearch({
  valuationHref,
  live,
  homes,
  items: frontDoor,
  forSale,
}: {
  valuationHref: string
  live?: HomeHeroLive
  /** Legacy: plain home rows ahead of the place seeds (no figures). */
  homes?: readonly MorphingSearchItem[]
  /**
   * What the search opens onto before a query (homeHeroSearchItems): towns
   * and communities with their live counts, the lead shelf's homes with their
   * photographs and asks. The panel then opens wide, over a scrim, with a
   * preview of the row under the cursor.
   */
  items?: readonly MorphingSearchItem[]
  /**
   * The hero eyebrow's region count (getForSaleCount), with its basis, for
   * the panel's search-everything door.
   */
  forSale?: ListingCount | null
}) {
  const router = useRouter()
  const uid = useId()
  const [query, setQuery] = useState('')
  const [sellAddress, setSellAddress] = useState('')
  const { suggestions } = useSearchSuggest(query)
  const items = useMemo(
    () =>
      flattenSuggestions(suggestions).filter(
        (item) =>
          item.kind === 'address' ||
          item.kind === 'city' ||
          item.kind === 'subdivision' ||
          item.kind === 'neighborhood' ||
          item.kind === 'zip',
      ),
    [suggestions],
  )
  const known = useMemo(() => {
    const map = new Map<string, MorphingSearchItem>()
    for (const item of frontDoor ?? []) {
      if (item.group === 'Towns' || item.group === 'Resorts and communities') {
        map.set(item.title.trim().toLowerCase(), item)
      }
    }
    return map
  }, [frontDoor])
  const morphItems = useMemo<V3MorphSearchItem[]>(() => {
    if (frontDoor?.length) {
      const typed = items.map((item) => homeHeroTypedItem(item, known))
      if (typed.length > 0) {
        // One row per place: a typed city and a typed subdivision can name the same door.
        const seen = new Set<string>()
        return typed.filter((row) => (seen.has(row.id) ? false : (seen.add(row.id), true)))
      }
      return [...frontDoor]
    }
    const typed = items.map((item) => ({
      id: item.href,
      title: item.label,
      description: item.sublabel,
    }))
    if (typed.length > 0) return typed
    const listed = homes?.length ? [...homes, ...PLACE_SEEDS] : PLACE_SEEDS
    return listed
  }, [items, homes, frontDoor, known])
  const wide = Boolean(frontDoor?.length)
  const regionalHref = publishRegionalSearchHref()
  const footer = wide ? (
    <>
      <a href={regionalHref} className="home-hero-search__all">
        {forSale && Number.isFinite(forSale.count) && forSale.count > 0
          ? // W1: the hero eyebrow's figure and basis (getForSaleCount).
            withCountBasis(`Search all ${formatCount(forSale.count)} listings for sale`, forSale.basis)
          : 'Search every listing for sale'}
      </a>
      <span className="home-hero-search__keys" aria-hidden="true">
        Arrow keys to move, Enter to open
      </span>
    </>
  ) : undefined

  const go = useCallback(
    (href: string) => {
      router.push(href)
    },
    [router],
  )

  const onBuySubmit = useCallback(() => {
    const text = query.trim()
    // A blank submit opens the regional list; trackSiteSearch records only words.
    trackSiteSearch({ surface: 'home_hero', source: 'submit', query: text })
    if (!text) {
      go(publishRegionalSearchHref())
      return
    }
    go(searchHrefForQuery(text))
  }, [query, go])

  const onSellSubmit = useCallback(() => {
    const address = sellAddress.trim()
    try {
      trackEvent('address_submit', { form: 'get-value', surface: 'home_hero' })
    } catch {
      // tracking helper missing in some envs
    }
    try {
      markAskSource('hero')
    } catch {
      // storage blocked — the submit still goes through, unattributed
    }
    if (!address) {
      go(valuationHref)
      return
    }
    const [path, hash] = valuationHref.split('#')
    const sep = path?.includes('?') ? '&' : '?'
    go(`${path}${sep}address=${encodeURIComponent(address)}${hash ? `#${hash}` : ''}`)
  }, [go, valuationHref, sellAddress])

  const buyFieldId = `${uid}-q`
  const sellFieldId = `${uid}-sell`
  const buyModeId = 'home-hero-mode-buy'
  const sellModeId = 'home-hero-mode-sell'
  const [mode, setMode] = useState('buy')
  const setModeAndRadio = useCallback((next: string) => {
    setMode(next)
    const sell = document.getElementById(sellModeId)
    const buy = document.getElementById(buyModeId)
    if (next === 'sell' && sell instanceof HTMLInputElement) {
      sell.checked = true
    } else if (buy instanceof HTMLInputElement) {
      buy.checked = true
    }
  }, [])
  useEffect(() => {
    const buy = document.getElementById(buyModeId)
    const sell = document.getElementById(sellModeId)
    const sync = () => setMode(sell instanceof HTMLInputElement && sell.checked ? 'sell' : 'buy')
    buy?.addEventListener('change', sync)
    sell?.addEventListener('change', sync)
    return () => {
      buy?.removeEventListener('change', sync)
      sell?.removeEventListener('change', sync)
    }
  }, [])

  return (
    <div className="home-hero-search" data-mode={mode} data-home-search="hero">
      <input
        type="radio"
        name={`${uid}-mode`}
        id={buyModeId}
        value="buy"
        defaultChecked
        className="home-hero-search__mode home-hero-search__mode--buy"
      />
      <input
        type="radio"
        name={`${uid}-mode`}
        id={sellModeId}
        value="sell"
        className="home-hero-search__mode home-hero-search__mode--sell"
      />

      {/* Inventory-count lecture under the search cut — Matt 2026-09-15. */}

      <V3Tabs
        label="Buy or sell"
        count={2}
        value={mode}
        onValueChange={setModeAndRadio}
        className="home-hero-search__tabs"
        items={[
          { value: 'buy', label: 'Buy', htmlFor: buyModeId },
          { value: 'sell', label: 'Sell', htmlFor: sellModeId },
        ]}
      />

      <form
        data-clarity-mask="True"
        action={BUY_ACTION}
        method="get"
        className="home-hero-search__panel-form home-hero-search__panel-form--buy"
        onSubmit={(event) => {
          event.preventDefault()
          onBuySubmit()
        }}
      >
        {/* Placeholder alone — no over-label above the field (Matt 2026-09-15). */}
        <label className="sr-only" htmlFor={buyFieldId}>
          Search homes
        </label>
        <V3MorphSearch
          placeholder="Bend, Tetherow, or an address"
          items={morphItems}
          onQueryChange={setQuery}
          onSelect={(item) => {
            // Picking a suggestion is a search too (V3ChromeSearch does the same);
            // only a typed-and-submitted query reached onBuySubmit's event.
            trackSiteSearch({ surface: 'home_hero', source: 'typeahead', query: item.title, term: query })
            go(item.id)
          }}
          {...(wide
            ? {
                panelMaxWidth: 1040,
                resultsMaxHeight: 500,
                scrim: true,
                footer,
                overlayClassName: 'home-hero-search-overlay',
              }
            : {})}
        >
          <div className="v3-morph-search__field">
            <input
              id={buyFieldId}
              className="home-hero-search__input"
              type="search"
              name="q"
              autoComplete="off"
              placeholder="Bend, Tetherow, or an address"
              defaultValue=""
            />
          </div>
          <button type="submit" className="v3-morph-search__go home-hero-search__go">
            Search
          </button>
        </V3MorphSearch>
      </form>

      <form
        data-clarity-mask="True"
        action={SELL_ACTION}
        method="get"
        className="home-hero-search__panel-form home-hero-search__panel-form--sell"
        onSubmit={(event) => {
          event.preventDefault()
          onSellSubmit()
        }}
      >
        <label className="home-hero-search__label" htmlFor={sellFieldId}>
          Value your home
        </label>
        <V3MorphSearch>
          <div className="v3-morph-search__field">
            <AddressAutocomplete
              id={sellFieldId}
              name="address"
              value={sellAddress}
              onChange={setSellAddress}
              placeholder="Street address"
              className="home-hero-search__input"
              wrapperClassName="home-hero-search__address"
            />
          </div>
          <button type="submit" className="v3-morph-search__go home-hero-search__go">
            Value my home
          </button>
        </V3MorphSearch>
      </form>
    </div>
  )
}
